import {
  Pixel,
  ScriptEvent,
  SessionEvent,
} from '@propps-au/pixel-analytics-types'
import { PixelFrontendClient } from '@propps-au/pixel-client'
import { Subject } from 'rxjs'
import { Script_ListingFragment } from '../../__generated__/sdk.generated'
import { EmbeddedFrameCommand } from '../frame'
import { hasAutoOpenHash } from './autoopen-hash'
import { render } from './renderer'

export async function mountListingTrigger({
  listing,
  container,
  target,
  forceMount,
  openOnMount,
  customMetadata = null,
  defaultButtonHost,
  framesChannel,
  pixel,
  location = window.location,
  internalMode,
  overrideLabel,
}: {
  listing: Script_ListingFragment
  container?: HTMLElement | null
  target?: HTMLElement | null
  forceMount?: boolean
  openOnMount?: boolean
  overridePath?: string
  customMetadata?: string | null
  defaultButtonHost: HTMLElement
  framesChannel: Subject<EmbeddedFrameCommand>
  pixel: PixelFrontendClient
  location?: Location
  internalMode?: boolean
  overrideLabel?: string
}) {
  await pixel.initialise()
  const sessionId = await pixel.getSessionId()
  await pixel.setCustomMetadata(customMetadata)

  const shouldHideButtonInternalMode = internalMode && !listing.published
  const shouldHideButtonExternalMode =
    !internalMode && !listing.shouldShowButtonOnWebsite

  if (
    (shouldHideButtonInternalMode && !forceMount) ||
    (shouldHideButtonExternalMode && !forceMount)
  ) {
    return { unmount: () => {}, listing }
  }

  const openFrame = () => {
    framesChannel.next({
      type: 'navigate',
      path: `/listing/${listing.id}`,
    })
    framesChannel.next({
      type: 'show',
    })
  }

  const onClick = () => {
    pixel.dispatch<Pixel.ScriptEvent.ButtonClicked>({
      type: ScriptEvent.BUTTON_CLICKED,
      mountType: 'native',
      appId: listing.source.appId,
      foreignId: listing.source.foreignId,
      listingId: listing.id,
      buttonLabel: listing.offerButtonLabel,
    })

    openFrame()
  }

  // Render the button
  const dispose = render({
    target,
    container,
    onClick,
    defaultButtonHost,
    label: overrideLabel ?? listing.offerButtonLabel,
  })

  // Emit mounted events
  pixel.dispatch<Pixel.ScriptEvent.ListingPageVisited>({
    type: ScriptEvent.LISTING_PAGE_VISITED,
    appId: listing.source.appId,
    foreignId: listing.source.foreignId,
    listingId: listing.id,
    url: location.href,
  })

  pixel.amplitude?.logEvent('button mounted', {
    listingId: listing.id,
    appId: listing.source.appId,
    foreignId: listing.source.foreignId,
    buttonLabel: listing.offerButtonLabel,
  })

  framesChannel.next({
    type: 'trigger-mounted',
    sessionId,
    appId: listing.source.appId,
    foreignId: listing.source.foreignId,
    listingId: listing.id,
  })

  // Forward session metadata
  framesChannel.next({
    type: 'set-session-metadata',
    customMetadata: customMetadata,
    search: location.search,
    referrer: {
      host: location.host,
      href: location.href,
      path: location.pathname,
    },
  })

  // Automatic open
  if (openOnMount || hasAutoOpenHash(location.hash, listing.id)) {
    openFrame()
  }

  // Attribution
  const search = new URLSearchParams(location.search)

  if (search.has('propps_atr_id')) {
    pixel.dispatch<Pixel.SessionEvent.SessionAttribution>({
      type: SessionEvent.SESSION_ATTRIBUTION,
      sessionId,
      // FIXME
      // @ts-ignore
      listingId: listing.id,
      attributionId: search.get('propps_atr_id')!,
    })
  }

  return { unmount: dispose, listing }
}
